import { useId, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { FieldLabelContext, InfoTip } from '@/shared/ui'
import { Info } from '@/shared/ui/icons'

/**
 * One labeled row of a create or edit form, from the mockups' `forms/FormRow.jsx`: the label in
 * a fixed column, right-aligned against the field on wider screens, stacked above it on narrow
 * ones. The fields inside take their name from the label (`FieldLabelContext`), and a required
 * row shows `*` after the field and says "required" to assistive technology.
 */
export function FormRow(props: {
  readonly label: string
  /** Shows the small info icon some legacy labels carry. */
  readonly info?: boolean
  /** An info button after the label, showing `text` as a tooltip; `label` is the button's name. */
  readonly tooltip?: { readonly label: string; readonly text: string }
  readonly required?: boolean
  readonly children: ReactNode
}) {
  const intl = useIntl()
  const labelId = useId()
  const required = props.required === true

  return (
    <div className="grid grid-cols-1 gap-1.5 py-1.5 sm:grid-cols-[220px_1fr] sm:items-center sm:gap-4">
      <span className="flex items-center gap-1.5 text-sm text-text-secondary sm:justify-end sm:text-right">
        {/* Only the label names the fields: an info button beside it stays out of their names. */}
        <span id={labelId}>
          {props.label}
          {required && <span className="sr-only">{` ${intl.formatMessage({ id: 'forms.required' })}`}</span>}
        </span>
        {props.info === true && <Info size={14} className="shrink-0 text-text-light" />}
        {props.tooltip !== undefined && <InfoTip label={props.tooltip.label} text={props.tooltip.text} />}
      </span>
      <FieldLabelContext value={labelId}>
        <div className="flex flex-wrap items-center gap-2">
          {props.children}
          {required && (
            <span className="text-text-primary" aria-hidden="true">
              *
            </span>
          )}
        </div>
      </FieldLabelContext>
    </div>
  )
}
